import React, { useCallback, useMemo, useState } from "react";

import { AddGroupIcon } from "../../icons";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { useThrottleFn } from "ahooks";

import "./index.less";

const noop = () => {};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

const MinusIcon = ({ size = 20, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    <path
      d="M4 10H16"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const PlusIcon = ({ size = 20, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M19.1666 9.16732H10.8333V0.833984H9.16659V9.16732H0.833252V10.834H9.16659V19.1673H10.8333V10.834H19.1666V9.16732Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * SeatPicker (Guests)
 *
 */
const SeatPicker = ({
  className = "",
  value,
  defaultValue = 0,
  min = 0,
  max = 8,
  disabled = false,
  placeholder = "How many guests?",
  onChange = noop,
  mobile = false,
}) => {
  const { t } = useTranslation();
  const isControlled = isNumber(value);
  const [innerValue, setInnerValue] = useState(() => {
    const initial = isNumber(defaultValue) ? defaultValue : min;
    return clamp(initial, min, max);
  });

  const currentValue = useMemo(() => {
    const raw = isControlled ? value : innerValue;
    const safe = isNumber(raw) ? raw : min;
    return clamp(safe, min, max);
  }, [isControlled, value, innerValue, min, max]);

  const { run: setValue } = useThrottleFn(
    (next) => {
      const nextValue = clamp(next, min, max);
      if (next >= max) {
        message.info({
          content: t("Header.Guest limit reached"),
          key: 'seat-picker-limit',
        });
      }
      if (!isControlled) setInnerValue(nextValue);
      onChange(nextValue);
    },
    { wait: 300 }
  );

  const canDec = !disabled && currentValue > min;
  const canInc = !disabled && currentValue < max;

  return (
    <div className={cn("ui-seat-picker", className, { "is-mobile": mobile })}>
      <div
        className={cn("ui-seat-picker__content", { "is-disabled": disabled })}
      >
        <div className="ui-seat-picker__label">
          <AddGroupIcon color="#8d8d8d" className="ui-seat-picker__icon" />
          {mobile ? (
            <span
              className="ui-seat-picker__placeholder"
              style={{ color: value ? "var(--text-color)" : null }}
            >
              {value
                ? t("Header.{count} Guests", { count: value })
                : t("Header.How many guests")}
            </span>
          ) : (
            <span className="ui-seat-picker__placeholder">
              {t("Header.How many guests")}
            </span>
          )}
        </div>

        <div
          className="ui-seat-picker__stepper"
          role="group"
          aria-label="Guests"
        >
          <button
            type="button"
            className="ui-seat-picker__stepperBtn"
            onClick={() => setValue(currentValue - 1)}
            disabled={!canDec}
            aria-label="Decrease guests"
          >
            <MinusIcon />
          </button>

          <div className="ui-seat-picker__stepperValue" aria-live="polite">
            {currentValue}
          </div>

          <button
            type="button"
            className="ui-seat-picker__stepperBtn"
            onClick={() => setValue(currentValue + 1)}
            data-disabled={!canInc}
            aria-label="Increase guests"
          >
            <PlusIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeatPicker;
